import { Link } from "react-router-dom";
import { FlagIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useGetToolboxTopicPlans } from "../../../api/queries";
import {
  SectionEmptyState,
  SectionHeader,
} from "../../../componentLibrary/SectionPanel";
import Spinner from "../../../componentLibrary/Spinner";
import Tag from "../../../componentLibrary/Tag";
import type { ToolboxPlanI } from "../../../types";

// TODO: replace with plan.tools once ToolboxPlanI has a tools field
const DEMO_TOOLS = ["Tool 1", "Tool 2", "Tool 3", "Tool 4"];
const MAX_VISIBLE_TOOLS = 3;

function PlanRow({ plan }: { plan: ToolboxPlanI }) {
  const visibleTools = DEMO_TOOLS.slice(0, MAX_VISIBLE_TOOLS);
  const overflowCount = DEMO_TOOLS.length - visibleTools.length;

  const goalText = plan.goal ? `To ${plan.goal}` : "No goal set";
  const cue = typeof plan.cue === "string" ? plan.cue.trim() : "";
  const timeframe =
    typeof plan.timeframe === "string" ? plan.timeframe.trim() : "";
  const whenText = cue ? `When ${cue}` : timeframe || "";

  return (
    <Link
      to={`plans/${plan._id}`}
      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 line-clamp-2">
          {goalText}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {whenText} <span>I will...</span>
        </p>
        <p className="mt-0.5 text-xs text-gray-500"></p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {visibleTools.map((tool) => (
            <Tag key={tool}>{tool}</Tag>
          ))}
          {overflowCount > 0 && <Tag variant="neutral">+{overflowCount}</Tag>}
        </div>
      </div>
    </Link>
  );
}

export default function MyPlansPanel({ topicId }: { topicId: string }) {
  const { data: plans, isLoading, isError } = useGetToolboxTopicPlans(topicId);

  const planList = plans ?? [];
  const hasPlans = !isLoading && !isError && planList.length > 0;

  return (
    <>
      <SectionHeader
        icon={<FlagIcon className="w-5 h-5 text-electrum-300 shrink-0" />}
        title="My Plans"
        action={hasPlans ? "Create plan" : undefined}
        actionTo={hasPlans ? "plans/new" : undefined}
      />

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner className="w-8" />
        </div>
      )}

      {isError && (
        <SectionEmptyState>
          <p className="text-sm text-gray-400 py-2">
            Could not load your plans.
          </p>
        </SectionEmptyState>
      )}

      {!isLoading && !isError && planList.length === 0 && (
        <SectionEmptyState>
          <p className="text-sm text-gray-400 py-2 mb-3">
            Create a plan to capture your goal and when you&apos;ll work on it.
          </p>
          <Link
            to="plans/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-electrum-100 px-3 py-1.5 text-sm font-bold text-electrum shadow transition-colors hover:bg-electrum-150"
          >
            <PlusIcon className="h-4 w-4" />
            Create plan
          </Link>
        </SectionEmptyState>
      )}

      {hasPlans && (
        <div className="space-y-3">
          {planList.map((plan) => (
            <PlanRow key={plan._id} plan={plan} />
          ))}
        </div>
      )}
    </>
  );
}
